import { apiSlice, TAG_TYPES } from '../api/apiSlice';

export const levelRankingApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getShopLevelRanking: builder.query({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.shopId) queryParams.append('shopId', params.shopId);
        if (params?.userId) queryParams.append('userId', params.userId);
        if (params?.platform) queryParams.append('platform', params.platform);
        const qs = queryParams.toString();
        return `/levelranking/progress${qs ? `?${qs}` : ''}`;
      },
      providesTags: (result) => [
        TAG_TYPES.LEVEL_RANKING || 'LevelRanking',
        TAG_TYPES.USER_CONNECTIONS || 'UserConnections',
        TAG_TYPES.CONNECTIONS || 'Connections',
        TAG_TYPES.SHOP || 'Shop',
        TAG_TYPES.MARKETER || 'Marketer',
        TAG_TYPES.MARKETERS || 'Marketers',
        TAG_TYPES.HIRE_REQUEST || 'HireRequest',
        TAG_TYPES.SUBSCRIPTION_MARKETING || 'SubscriptionMarketing',
        TAG_TYPES.SHOP_RATING || 'ShopRating',
        { type: 'Shop', id: result?.selectedShop?._id || 'CURRENT_SHOP' },
        { type: 'User', id: result?.user?._id || 'CURRENT_USER' },
        { type: 'LevelRanking', id: result?.selectedShop?._id || 'CURRENT_SHOP' },
      ],
    }),

    /**
     * Mark a milestone level (e.g., Level 1 Bronze Starter) as completed.
     * Includes optimistic update for 0ms realtime UI update without page reload.
     */
    completeLevel: builder.mutation({
      query: (body) => ({
        url: '/levelranking/complete-level',
        method: 'POST',
        body,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        // Optimistic UI cache patch
        const patchResult = dispatch(
          levelRankingApiSlice.util.updateQueryData(
            'getShopLevelRanking',
            { shopId: arg.shopId, userId: arg.userId, platform: arg.platform },
            (draft) => {
              if (draft?.progression) {
                const targetLvl = Number(arg.level) || 1;
                draft.progression.completedLevelsCount = Math.max(
                  targetLvl,
                  draft.progression.completedLevelsCount || 0
                );
                if (targetLvl >= 1) {
                  draft.progression.isLevel1Completed = true;
                }
                if (draft.progression.stepper && draft.progression.stepper[0]) {
                  draft.progression.stepper[0].state = 'done';
                }
                if (draft.progression.tiers && draft.progression.tiers[0]) {
                  draft.progression.tiers[0].status = 'completed';
                  draft.progression.tiers[0].subs = ['(✓ Met)', '(✓ Met)', '(✓ Met)'];
                }
              }
            }
          )
        );

        try {
          await queryFulfilled;
          const payload = {
            shopId: arg.shopId,
            level: arg.level || 1,
            timestamp: Date.now(),
          };
          if (typeof window !== 'undefined') {
            try {
              localStorage.setItem('last_milestone_action', JSON.stringify(payload));
            } catch (_) {}
            window.dispatchEvent(
              new CustomEvent('selectezi:level_ranking_update', { detail: payload })
            );
            if (typeof BroadcastChannel !== 'undefined') {
              try {
                const bc = new BroadcastChannel('selectezi_level_ranking');
                bc.postMessage(payload);
                bc.close();
              } catch (_) {}
            }
          }
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              TAG_TYPES.LEVEL_RANKING || 'LevelRanking',
              TAG_TYPES.SHOP || 'Shop',
              { type: 'Shop', id: arg.shopId },
              { type: 'LevelRanking', id: arg.shopId },
            ],
    }),
  }),
});

export const {
  useGetShopLevelRankingQuery,
  useLazyGetShopLevelRankingQuery,
  useCompleteLevelMutation,
} = levelRankingApiSlice;
